import React from 'react';
import { Controller, DefaultValues, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  addReportToDashboardSchema,
  IAddReportToDashboardFormState,
} from './types';
import {
  FieldLabel,
  VerticalField,
  InputField,
  TextAreaField,
} from '../../../Form';
import { ModalActions } from '../../../KubitModal/KubitModalUI';
import { PrimaryButton, SecondaryButton } from '../../../Button';
import AddToDashboardSelect from './AddToDashboardSelect';
import usePermission from '../../../../utils/hooks/permissionHooks';
import { PermissionKey } from '../../../../utils/apollo/graphqlSchema';
import PermissionElementWrapper from '../../../../pages/PermissionControls/PermissionElementWrapper';
import { NEW_DASHBOARD_SETTINGS } from './utils';

interface IProps {
  defaultValues: DefaultValues<IAddReportToDashboardFormState>;
  onSubmit: (
    formData: IAddReportToDashboardFormState,
    shouldRedirect: boolean,
  ) => void;
  onReset: () => void;
}

const AddReportToDashboardForm = ({
  defaultValues,
  onSubmit,
  onReset,
}: IProps) => {
  const { checkIsPermitted } = usePermission();

  const isPermittedToCreateDashboard = checkIsPermitted({
    requiredPermissionKeys: [PermissionKey.DashboardCreate],
  });
  const isPermittedToEditDashboard = checkIsPermitted({
    requiredPermissionKeys: [PermissionKey.DashboardEdit],
  });

  const formMethods = useForm<IAddReportToDashboardFormState>({
    resolver: zodResolver(addReportToDashboardSchema),
    defaultValues,
  });

  const {
    handleSubmit,
    formState: { isSubmitting, isValid },
    control,
    watch,
  } = formMethods;

  const baseId = watch('baseId');

  const canCreateOrEditDashboard =
    baseId === NEW_DASHBOARD_SETTINGS.key
      ? isPermittedToCreateDashboard
      : isPermittedToEditDashboard;

  return (
    <form>
      <Controller
        name="name"
        control={control}
        render={({ field, fieldState }) => (
          <VerticalField>
            <FieldLabel>Report name</FieldLabel>
            <InputField
              {...field}
              autoComplete="off"
              placeholder={'Add name'}
              hasError={Boolean(fieldState.error)}
            />
          </VerticalField>
        )}
      />
      <Controller
        name="description"
        control={control}
        render={({ field, fieldState }) => (
          <VerticalField>
            <FieldLabel>Report description</FieldLabel>
            <TextAreaField
              {...field}
              placeholder={'Add description'}
              minRows={2}
              maxRows={5}
              hasError={Boolean(fieldState.error)}
            />
          </VerticalField>
        )}
      />
      <Controller
        name="baseId"
        control={control}
        render={({ field, fieldState }) => (
          <VerticalField>
            <FieldLabel>Add to Dashboard</FieldLabel>
            <AddToDashboardSelect
              value={field.value}
              onChange={field.onChange}
              hasError={Boolean(fieldState.error)}
            />
          </VerticalField>
        )}
      />
      <ModalActions>
        <SecondaryButton
          type="button"
          onClick={onReset}
          disabled={isSubmitting}
          trackContext="reports"
          trackAction="add to dashboard - cancel"
        >
          Cancel
        </SecondaryButton>
        <PermissionElementWrapper isPermitted={canCreateOrEditDashboard}>
          <PrimaryButton
            type="button"
            disabled={!canCreateOrEditDashboard || isSubmitting}
            isGreyedOut={!isValid}
            onClick={handleSubmit((data) => onSubmit(data, true))}
            trackContext="reports"
            trackAction="add to dashboard - save & go"
          >
            Save & Go
          </PrimaryButton>
        </PermissionElementWrapper>
        <PermissionElementWrapper isPermitted={canCreateOrEditDashboard}>
          <PrimaryButton
            type="button"
            disabled={!canCreateOrEditDashboard || isSubmitting}
            isGreyedOut={!isValid}
            onClick={handleSubmit((data) => onSubmit(data, false))}
            trackContext="reports"
            trackAction="add to dashboard - save"
          >
            Save
          </PrimaryButton>
        </PermissionElementWrapper>
      </ModalActions>
    </form>
  );
};

export default AddReportToDashboardForm;
